import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { CaretLeft, Plus, Trash, Warning } from "@phosphor-icons/react";

function fmtLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function prevMondayISO() {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1) - day - 7);
  return fmtLocal(d);
}

function snapToMonday(dateStr) {
  if (!dateStr) return dateStr;
  const parts = dateStr.split("-").map(Number);
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  const wd = d.getDay();
  const diff = (wd === 0 ? -6 : 1) - wd;
  d.setDate(d.getDate() + diff);
  return fmtLocal(d);
}

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function ContractorTimesheet() {
  const nav = useNavigate();
  const { id } = useParams(); // edit mode
  // Backend hydrates each assignment with _project_name / _client_name /
  // _project_location / _project_po for contractors — we DON'T re-fetch
  // /projects or /clients here because those endpoints are admin-only and
  // silently return [] for contractors, which is what caused the dropdown
  // to fall back to raw UUIDs.
  const [assignments, setAssignments] = useState([]);
  const [assignmentId, setAssignmentId] = useState("");
  const defaultWeek = useMemo(() => prevMondayISO(), []);
  const [weekStart, setWeekStart] = useState(defaultWeek);
  const [weekWarn, setWeekWarn] = useState(null);
  const [hours, setHours] = useState({ mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 });
  const [perDiem, setPerDiem] = useState(0);
  const [exceptions, setExceptions] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/assignments").then((r) => setAssignments(Array.isArray(r.data) ? r.data : []));
    if (id) api.get(`/timesheets/${id}`).then((r) => {
      const t = r.data;
      setAssignmentId(t.assignment_id); setWeekStart(t.week_start);
      setHours({ mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0, ...t.hours });
      setPerDiem(t.per_diem_days); setExceptions(t.exceptions || []);
    });
  }, [id]);

  const total = useMemo(() => DAYS.reduce((s, d) => s + (parseFloat(hours[d]) || 0), 0), [hours]);
  const assignment = assignments.find((a) => a.id === assignmentId);
  const sups = assignment?._project_supervisors || [];
  const [supervisorEmail, setSupervisorEmail] = useState("");
  const [sug, setSug] = useState({ name: "", email: "", phone: "" });
  const [sugBusy, setSugBusy] = useState(false);
  const [sugSent, setSugSent] = useState(false);
  const noApprover = !id && !!assignment && sups.length === 0;

  const sendSuggestion = async () => {
    setErr(""); setSugBusy(true);
    try {
      await api.post("/timesheets/suggest-supervisor", {
        assignment_id: assignmentId,
        name: sug.name.trim(), email: sug.email.trim(), phone: sug.phone.trim() || null,
      });
      setSugSent(true);
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); }
    finally { setSugBusy(false); }
  };

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = { assignment_id: assignmentId, week_start: weekStart, hours, per_diem_days: parseInt(perDiem) || 0, exceptions };
      if (!id && supervisorEmail) payload.supervisor_email = supervisorEmail;
      if (id) await api.post(`/timesheets/${id}/resubmit`, payload);
      else await api.post("/timesheets", payload);
      nav("/contractor");
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white">
      <header className="px-4 py-4 border-b border-slate-200 flex items-center gap-3 bg-slate-900 text-white sticky top-0 z-10">
        <Link to="/contractor" className="p-2 text-slate-300 hover:text-white"><CaretLeft size={22} /></Link>
        <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-8 w-8 rounded object-contain bg-white/10 p-0.5" />
        <div className="font-bold text-lg">{id ? "Resubmit timesheet" : "New timesheet"}</div>
      </header>
      <div className="p-5">
        <label className="tinylabel">Project</label>
        <select value={assignmentId} onChange={(e) => { setAssignmentId(e.target.value); setSupervisorEmail(""); setSugSent(false); }}
          data-testid="ts-assignment" disabled={!!id}
          className="mt-1 w-full h-14 px-3 border border-slate-300 rounded-md text-base disabled:bg-slate-100">
          <option value="">Select a project…</option>
          {assignments.map((a) => {
            const projectLabel = a._project_name || "Project";
            const clientLabel = a._client_name ? ` — ${a._client_name}` : "";
            return <option key={a.id} value={a.id}>{projectLabel}{clientLabel}</option>;
          })}
        </select>

        {assignment && (
          <div className="mt-4 border border-slate-200 rounded-md p-4 bg-slate-50 text-sm space-y-1"
               data-testid="ts-assignment-summary">
            {assignment._client_name && <div><b>Client:</b> {assignment._client_name}</div>}
            <div>
              <b>Project:</b> {assignment._project_name || "—"}
              {assignment._project_po ? ` · PO ${assignment._project_po}` : ""}
            </div>
            {assignment._project_location && <div><b>Location:</b> {assignment._project_location}</div>}
            {!id && sups.length === 1 && (
              <div data-testid="ts-single-supervisor"><b>Approver:</b> {sups[0].name || sups[0].email}</div>
            )}
          </div>
        )}

        {!id && sups.length > 1 && (
          <div className="mt-4">
            <label className="tinylabel block">Who should approve this timesheet?</label>
            <select value={supervisorEmail} onChange={(e) => setSupervisorEmail(e.target.value)}
              data-testid="ts-supervisor"
              className={`mt-1 w-full h-14 px-3 border rounded-md text-base ${supervisorEmail ? "border-slate-300" : "border-amber-400 bg-amber-50"}`}>
              <option value="">Select your supervisor…</option>
              {sups.map((s) => (
                <option key={s.email} value={s.email}>{s.name ? `${s.name} (${s.email})` : s.email}</option>
              ))}
            </select>
            {!supervisorEmail && (
              <div data-testid="ts-supervisor-required-note" className="mt-1 text-xs font-bold text-amber-700">
                This project has more than one supervisor — pick who approves your hours before submitting.
              </div>
            )}
          </div>
        )}

        {noApprover && (
          <div data-testid="ts-no-supervisor-note"
               className="mt-4 border-l-4 border-rose-500 bg-rose-50 rounded-r px-4 py-3">
            <div className="text-sm font-bold text-rose-800">There is no approving supervisor assigned to this project.</div>
            <p className="mt-1 text-xs text-rose-700 font-semibold leading-relaxed">
              PLEASE NOTIFY <a href="mailto:billing@safetyonsitesolutions.com" className="underline">billing@safetyonsitesolutions.com</a>{" "}
              OF THE NAME, EMAIL AND CELL PHONE NUMBER OF THE INDIVIDUAL WHO WILL BE APPROVING YOUR
              TIMESHEET. You will be able to submit your timesheet once that information is updated.
            </p>
            {sugSent ? (
              <div data-testid="suggest-sent-note" className="mt-3 text-sm font-bold text-emerald-700">
                ✓ Sent to the office. Once they add the approver, come back and submit your timesheet.
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <div className="text-xs font-bold text-slate-700">Or send the details right here:</div>
                <input value={sug.name} onChange={(e) => setSug({ ...sug, name: e.target.value })}
                  placeholder="Approver's name" data-testid="suggest-name"
                  className="w-full h-12 px-3 border border-slate-300 rounded-md text-base bg-white" />
                <input type="email" value={sug.email} onChange={(e) => setSug({ ...sug, email: e.target.value })}
                  placeholder="Approver's email" data-testid="suggest-email"
                  className="w-full h-12 px-3 border border-slate-300 rounded-md text-base bg-white" />
                <input type="tel" value={sug.phone} onChange={(e) => setSug({ ...sug, phone: e.target.value })}
                  placeholder="Approver's cell phone" data-testid="suggest-phone"
                  className="w-full h-12 px-3 border border-slate-300 rounded-md text-base bg-white" />
                <button type="button" data-testid="suggest-send-btn"
                  disabled={sugBusy || !sug.name.trim() || !sug.email.trim()}
                  onClick={sendSuggestion}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md disabled:opacity-50">
                  {sugBusy ? "Sending…" : "Send to the office"}
                </button>
              </div>
            )}
          </div>
        )}

        <label className="tinylabel mt-6 block">Week starting (Mon)</label>
        <input type="date" value={weekStart}
          onChange={(e) => {
            const snapped = snapToMonday(e.target.value);
            if (!snapped) return;
            if (snapped !== defaultWeek) setWeekWarn(snapped);
            else { setWeekStart(snapped); }
          }}
          data-testid="ts-week" className="mt-1 w-full h-14 px-3 border border-slate-300 rounded-md text-base" disabled={!!id} />
        {!id && (
          <div className="mt-1 text-xs text-slate-500">
            Defaults to <b>last week</b> — Monday {defaultWeek}. Dates snap to the Monday of the week you pick.
          </div>
        )}
        {!id && weekStart !== defaultWeek && (
          <div data-testid="ts-week-changed-note" className="mt-2 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 flex items-center gap-2">
            <Warning size={16} weight="fill" className="text-amber-500 shrink-0" />
            Week changed to Monday {weekStart} — this is NOT last week's timesheet.
          </div>
        )}

        <label className="tinylabel mt-6 block">Hours per day</label>
        <div className="grid grid-cols-2 gap-3 mt-1">
          {DAYS.map((d) => (
            <div key={d}>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{d}</div>
              <input type="number" min="0" step="0.25" value={hours[d]} onChange={(e) => setHours({ ...hours, [d]: e.target.value })}
                data-testid={`ts-hours-${d}`} className="mt-1 w-full h-14 px-3 border border-slate-300 rounded-md text-lg font-mono" />
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3">
          <div className="tinylabel">Total hours</div>
          <div data-testid="ts-total-hours" className="text-2xl font-black font-mono text-green-600">{total.toFixed(2)}</div>
        </div>

        <label className="tinylabel mt-6 block">Per-diem days</label>
        <input type="number" min="0" value={perDiem} onChange={(e) => setPerDiem(e.target.value)}
          data-testid="ts-per-diem" placeholder="Enter number of days (not dollars)"
          className="mt-1 w-full h-14 px-3 border border-slate-300 rounded-md text-lg font-mono" />
        <div className="mt-1 text-xs text-slate-500">Enter the <b>number of days</b> you are claiming per diem — not a dollar amount.</div>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <label className="tinylabel">Exceptions (optional)</label>
            <button type="button" onClick={() => setExceptions([...exceptions, { description: "", amount: 0 }])}
              data-testid="add-exception-btn" className="text-green-600 font-semibold flex items-center gap-1"><Plus size={16} /> Add</button>
          </div>
          {exceptions.map((ex, i) => (
            <div key={i} className="flex gap-2 items-center mt-2">
              <input placeholder="Description" value={ex.description} onChange={(e) => { const c = [...exceptions]; c[i] = { ...c[i], description: e.target.value }; setExceptions(c); }} className="flex-1 h-12 px-3 border border-slate-300 rounded-md" />
              <input type="number" step="0.01" placeholder="$" value={ex.amount} onChange={(e) => { const c = [...exceptions]; c[i] = { ...c[i], amount: parseFloat(e.target.value) || 0 }; setExceptions(c); }} className="w-28 h-12 px-3 border border-slate-300 rounded-md" />
              <button type="button" onClick={() => setExceptions(exceptions.filter((_, j) => j !== i))} className="p-2 text-red-600"><Trash size={20} /></button>
            </div>
          ))}
        </div>

        {err && <div className="text-red-600 text-sm mt-4">{err}</div>}
        <button data-testid="submit-timesheet-btn" onClick={submit}
          disabled={!assignmentId || busy || noApprover || (!id && sups.length > 1 && !supervisorEmail)}
          className="mt-6 w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg font-bold rounded-lg transition-transform active:scale-95 disabled:opacity-60">
          {busy ? "Submitting…" : id ? "Resubmit" : "Submit"}
        </button>
      </div>

      {weekWarn && (
        <div className="fixed inset-0 bg-slate-900/70 flex items-center justify-center p-4 z-50" data-testid="week-override-modal">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 border-2 border-amber-400 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-3">
              <Warning size={36} weight="fill" className="text-amber-500 shrink-0" />
              <h2 className="text-xl font-black text-slate-900">Change the week?</h2>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              Timesheets are for <b>last week</b> — Monday <b>{defaultWeek}</b>.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed mt-2">
              You picked the week of Monday <b className="text-amber-700">{weekWarn}</b>.
              Only continue if you are sure this timesheet is for a different week —
              the wrong date delays your pay and mis-reports your time to the client.
            </p>
            <div className="mt-5 space-y-2">
              <button type="button" data-testid="week-override-keep"
                onClick={() => { setWeekStart(defaultWeek); setWeekWarn(null); }}
                className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg">
                Keep last week ({defaultWeek})
              </button>
              <button type="button" data-testid="week-override-confirm"
                onClick={() => { setWeekStart(weekWarn); setWeekWarn(null); }}
                className="w-full h-12 border-2 border-amber-400 text-amber-800 font-bold rounded-lg hover:bg-amber-50">
                Yes, I'm sure — use {weekWarn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
